/**
 * ChatMessageList Component
 *
 * Renders the list of chat messages with markdown support, loading indicators,
 * and metadata display for data sources used.
 */

import React, { useRef, useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { message } from 'antd';
import ChartRenderer from './ChartRenderer';
import copyIcon from '../../../../assets/copy.svg';
import getFileTypeIcon from '../../utils/getFileTypeIcon';

/**
 * TableWithCopyButton Component
 * Wraps markdown tables with a copy button in the top-right corner
 */
const TableWithCopyButton = ({ children }) => {
  const [isCopying, setIsCopying] = useState(false);

  const handleCopy = async () => {
    try {
      setIsCopying(true);

      // Extract table data from children
      const table = children?.props?.children;
      if (!table) return;

      // Build CSV representation of table
      let csvContent = '';

      // Helper to extract text from React elements recursively
      const extractText = (node) => {
        if (typeof node === 'string') return node;
        if (Array.isArray(node)) return node.map(extractText).join('');
        if (node?.props?.children) return extractText(node.props.children);
        return '';
      };

      // Helper to escape CSV cell content
      const escapeCsvCell = (cell) => {
        const cellText = String(cell);
        // If cell contains comma, quote, or newline, wrap in quotes and escape quotes
        if (cellText.includes(',') || cellText.includes('"') || cellText.includes('\n')) {
          return `"${cellText.replace(/"/g, '""')}"`;
        }
        return cellText;
      };

      // Process table rows
      React.Children.forEach(table, (section) => {
        if (!section?.props?.children) return;

        React.Children.forEach(section.props.children, (row) => {
          if (!row?.props?.children) return;

          const cells = [];
          React.Children.forEach(row.props.children, (cell) => {
            const cellContent = extractText(cell.props?.children || '');
            cells.push(escapeCsvCell(cellContent));
          });

          csvContent += cells.join(',') + '\n';
        });
      });

      await navigator.clipboard.writeText(csvContent.trim());
      message.success('Table copied to clipboard as CSV');
    } catch (err) {
      console.error('Failed to copy table:', err);
      message.error('Failed to copy table');
    } finally {
      setIsCopying(false);
    }
  };

  return (
    <div className="table-with-copy-wrapper">
      <button
        className="table-copy-button"
        onClick={handleCopy}
        disabled={isCopying}
        title="Copy table to clipboard"
      >
        <img src={copyIcon} alt="Copy" width="16" height="16" />
      </button>
      <div className="table-with-copy">
        {children}
      </div>
    </div>
  );
};

const ChatMessageList = ({
  messages = [],
  loading = false,
  stageName = 'Initializing...',
  elapsedSeconds = 0,
  onCancelRequest = null,
  sessionSource = null
}) => {
  const messagesEndRef = useRef(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current && messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Custom renderers for ReactMarkdown to add copy button to tables
  const markdownComponents = {
    table: ({ node, ...props }) => (
      <TableWithCopyButton>
        <table {...props} />
      </TableWithCopyButton>
    )
  };

  return (
    <div className="reports-chatbot-messages">
      {messages.map((msg, index) => {
        return (
          <div
            key={index}
            className={`reports-chatbot-message ${msg.role} ${msg.isError ? 'error' : ''}`}
            data-testid={`ai-message-${msg.role}`}
          >
            <div className="reports-chatbot-message-content">
              {msg.role === 'assistant' ? (
                <>
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={markdownComponents}
                  >
                    {msg.content}
                  </ReactMarkdown>

                  {/* Render charts if present */}
                  {msg.metadata && msg.metadata.hasCharts && msg.metadata.charts && msg.metadata.charts.charts && (
                    <div className="reports-charts-section">
                      {msg.metadata.charts.charts.map((chart) => (
                        <ChartRenderer key={chart.id} chartSpec={chart} />
                      ))}
                    </div>
                  )}
                </>
              ) : (
                msg.content
              )}
            </div>
            
            {(msg.metadata?.contextType || msg.metadata?.dataSourcesUsed || msg.metadata?.attachments) && (
              <div className="reports-chatbot-message-meta">
                {msg.metadata?.contextType && (
                  <span className={`context-badge context-${msg.metadata.contextType}`}>
                    {msg.metadata.contextType === 'subject'
                      ? (msg.metadata.contextIdentifier
                          ? `Subject: ${msg.metadata.contextIdentifier}`
                          : 'Subject Management')
                      : 'Analytics'}
                  </span>
                )}
                {msg.metadata?.attachments && msg.metadata.attachments.length > 0 && (
                  msg.metadata.attachments.map((att, i) => (
                    <span key={i} className="attachment-badge">
                      {getFileTypeIcon(att.fileName, att.mimeType)}
                      <span className="attachment-name">{att.fileName}</span>
                    </span>
                  ))
                )}
                {msg.metadata?.dataSourcesUsed && msg.metadata.dataSourcesUsed.length > 0 && (
                  <span className="data-sources">
                    Data: {msg.metadata.dataSourcesUsed.map(ds => typeof ds === 'string' ? ds : ds.type).join(', ')}
                  </span>
                )}
                {msg.metadata?.recordsAnalyzed > 0 && (
                  <span className="records-count">
                    {msg.metadata.recordsAnalyzed} records
                  </span>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Loading indicator */}
      {loading && (
        <div className="reports-chatbot-message assistant loading">
          <div className="reports-chatbot-loading">
            <svg className="loading-spinner" width="18" height="18" viewBox="0 0 16 16" fill="none">
              <path d="M8 0.75V3.25" stroke="#6c6c89" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path opacity="0.88" d="M13.127 2.873L11.359 4.641" stroke="#6c6c89" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path opacity="0.75" d="M15.25 8H12.75" stroke="#6c6c89" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path opacity="0.63" d="M13.127 13.127L11.359 11.359" stroke="#6c6c89" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path opacity="0.5" d="M8 15.25V12.75" stroke="#6c6c89" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path opacity="0.38" d="M2.873 13.127L4.641 11.359" stroke="#6c6c89" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path opacity="0.25" d="M0.75 8H3.25" stroke="#6c6c89" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path opacity="0.13" d="M2.873 2.873L4.641 4.641" stroke="#6c6c89" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <div className="loading-text">
              <span className="stage-name">{stageName}</span>
              <span className="elapsed-time">
                {elapsedSeconds.toFixed(0)}s
              </span>
            </div>
            {elapsedSeconds > 20 && onCancelRequest && (
              <button
                onClick={onCancelRequest}
                className="cancel-button"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      )}

      <div ref={messagesEndRef} />
    </div>
  );
};

export default ChatMessageList;
